<template>
  <!-- KOL申请弹窗 -->
  <transition name="fade" mode="out-in">
    <div class="popup feedback-popup kol-popup" v-show="visible">
      <div class="popup-mask" @click="closePopup"></div>
      <div class="popup-content">
        <div class="popup-content-close" @click="closePopup"></div>
        <div class="popup-title">
          <div class="popup-title-icon"></div>
        <div class="popup-title-text">{{ langData.kolPopupTitle }}</div>
        </div>
        <div class="content-area">
          <!-- 当前账号 -->
          <div class="form-item">
            <label class="form-label">{{ langData.kolAccount }}</label>
            <div class="account-info">{{ currentAccount }}</div>
          </div>

          <!-- 电子邮箱 -->
          <div class="form-item">
            <label class="form-label required">{{ langData.kolEmail }}</label>
            <input
              class="form-input"
              type="email"
              v-model="formData.email"
              :placeholder="langData.kolEmailPlaceholder"
              required
            />
          </div>

          <!-- Discord User ID -->
          <div class="form-item">
            <label class="form-label required">{{ langData.kolDiscordId }}</label>
            <input
              class="form-input"
              type="text"
              v-model="formData.discordUserName"
              :placeholder="langData.kolDiscordIdPlaceholder"
              required
            />
            <img
              class="question-icon"
              src="@images/popup/question.png"
              alt="?"
              @click="showDiscordHelp = true"
            />
          </div>

          <!-- 创作专长 -->
          <div class="form-item">
            <label class="form-label required">{{ langData.kolSpecialty }}</label>
            <div class="select-box">
              <select v-model="formData.specialtyType" required>
                <option value="" disabled hidden>{{ langData.kolSpecialtyPlaceholder }}</option>
                <option value="1">{{ langData.kolSpecialty1 }}</option>
                <option value="2">{{ langData.kolSpecialty2 }}</option>
                <option value="3">{{ langData.kolSpecialty3 }}</option>
                <option value="4">{{ langData.kolSpecialty4 }}</option>
              </select>
            </div>
          </div>

          <!-- 过往作品链接 -->
          <div class="form-item form-item-vertical">
            <label class="form-label required">{{ langData.kolWorkUrl }}</label>
            <textarea
              class="text-input"
              v-model="formData.workUrlList"
              :placeholder="langData.kolWorkUrlPlaceholder"
              maxlength="1000"
              required
            ></textarea>
            <div class="char-count">{{ formData.workUrlList.length }}/1000</div>
          </div>

          <!-- 订阅者数量 -->
          <div class="form-item">
            <label class="form-label required">{{ langData.kolFollower }}</label>
            <input
              class="form-input"
              type="number"
              v-model="formData.followerTotal"
              :placeholder="langData.kolFollowerPlaceholder"
              min="0"
              required
            />
          </div>
        </div>

        <div class="popup-bottom">
          <div
            class="confirm-btn btns"
            @click="submitApplication"
            :class="{ disabled: isSubmitting }"
          >{{ langData.kolSubmitBtn }}</div>
          <div
            class="cancel-btn btns"
            @click="closePopup"
            :class="{ disabled: isSubmitting }"
          >{{ langData.cancelBtn }}</div>
        </div>
      </div>
    </div>
  </transition>

  <!-- Discord帮助弹窗 -->
  <transition name="fade" mode="out-in">
    <div
      class="discord-help-popup"
      v-show="showDiscordHelp"
      @click="showDiscordHelp = false"
    >
      <div class="discord-help-content" @click.stop>
        <img
          class="discord-help-image"
          :src="discordHelpImage"
          alt="Discord用户名说明"
        />
        <div class="discord-help-close" @click="showDiscordHelp = false">×</div>
      </div>
    </div>
  </transition>

  <!-- 退出确认弹窗 -->
  <exitConfirmPopup
    :visible="showExitConfirm"
    @update:visible="showExitConfirm = $event"
    @confirm="handleExitConfirm"
  />
</template>

<script setup>
import { ref, reactive, watch, inject, computed } from "vue";
import { getJSON } from "@api/request";
import { getUrlParams, getActivityApi } from "@utils";
import LanguageHook from "@hooks/language";
import exitConfirmPopup from "./exitConfirmPopup.vue";

// 定义 props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  userInfo: {
    type: Object,
    default: () => ({}),
  },
});

// 定义 emits
const emit = defineEmits(["update:visible", "showTip"]);

// 语言相关
const { langData, curLang } = LanguageHook();

const discordHelpImageMap = {
  'zh-CN': new URL('@images/popup/dicord-popup-zh-CN.png', import.meta.url).href,
  'th-TH': new URL('@images/popup/dicord-popup-th-TH.png', import.meta.url).href,
};
const discordHelpImage = computed(() =>
  discordHelpImageMap[curLang.value] ||
  new URL('@images/popup/dicord-popup-en-US.png', import.meta.url).href
);

// 响应式数据
const formData = reactive({
  email: "",
  discordUserName: "",
  specialtyType: "",
  workUrlList: "",
  followerTotal: "",
});

const isSubmitting = ref(false);
const showDiscordHelp = ref(false);
const showExitConfirm = ref(false);

// 计算当前账号显示
const currentAccount = computed(() => {
  if (props.userInfo && props.userInfo.username) {
    return `${props.userInfo.username || ""}`;
  }
  return langData.notLoggedIn || "未登录";
});

// 重置表单
const reset = () => {
  formData.email = "";
  formData.discordUserName = "";
  formData.specialtyType = "";
  formData.workUrlList = "";
  formData.followerTotal = 0;
};

// 监听弹窗显示状态
watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible) {
      // 弹窗打开时可以做一些初始化操作
      console.log("KOL申请弹窗已打开");
    }
  }
);

// 检查表单是否有内容
const hasFormData = () => {
  return (
    formData.email ||
    formData.discordUserName ||
    formData.specialtyType ||
    formData.workUrlList ||
    formData.followerTotal
  );
};

// 关闭弹窗 - 如果有表单内容则显示确认弹窗
const closePopup = (showConfirm = true) => {
  if (showConfirm) {
    showExitConfirm.value = true;
  } else {
    // 直接关闭
    emit("update:visible", false);
  }
};

// 确认退出处理
const handleExitConfirm = () => {
  reset();
  emit("update:visible", false);
};

// 提交申请
const submitApplication = async () => {
  // 验证必填项
  if (!formData.email) {
    emit("showTip", langData.kolErrEmail);
    return;
  }

  // 验证邮箱格式
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formData.email)) {
    emit("showTip", langData.kolErrEmailFormat);
    return;
  }

  if (!formData.discordUserName) {
    emit("showTip", langData.kolErrDiscord);
    return;
  }

  if (!formData.specialtyType) {
    emit("showTip", langData.kolErrSpecialty);
    return;
  }

  if (!formData.workUrlList || formData.workUrlList.trim() === "") {
    emit("showTip", langData.kolErrWorkUrl);
    return;
  }

  // 验证订阅者数量为正整数
  const followerNum = parseInt(formData.followerTotal);
  if (isNaN(followerNum) || followerNum < 0) {
    emit("showTip", langData.kolErrFollower);
    return;
  }

  try {
    window.activityDataLayer.push({
      eventName: "click_button",
      eventType: "KOL申請",
      eventReason: "KOL申請",
    });
    // 显示提交中状态
    isSubmitting.value = true;

    // 准备提交数据
    const params = {
      email: formData.email,
      discordUserName: encodeURIComponent(
        encodeURIComponent(formData.discordUserName)
      ),
      specialtyType: formData.specialtyType,
      workUrlList: encodeURIComponent(formData.workUrlList), // 必填字段
      followerTotal: followerNum.toString(),
    };

    console.log("提交KOL申请参数:", params);

    // 调用KOL申请API
    const { data, result, code } = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "joinTeam"),
      {
        params: JSON.stringify(params),
      }
    );

    console.log("KOL申请API响应:", { data, result, code });

    if (+result === 1) {
      emit("showTip", langData.kolSubmitSuccess);
      // 清空表单
      reset();
      closePopup(false);
    } else {
      emit(
        "showTip",
        `[${code}]${
          langData[Math.abs(+code)] ||
          data?.message ||
          langData.kolSubmitFail
        }`
      );
    }
  } catch (error) {
    console.error("提交KOL申請異常:", error);
    emit("showTip", langData.kolNetworkFail);
  } finally {
    isSubmitting.value = false;
  }
};
</script>
